import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { ChevronLeft, ChevronRight, LoaderCircle, RotateCw, X } from "lucide-react";
import { AssistOverlay, type NormalizedRegion } from "./reader-support";
import { fitMangaPanelToViewport, type MangaPanel } from "./manga-panels";
import { readerPageOcclusionShift, type SupportPanelBounds } from "./reader-occlusion";
import type { MangaPage, OcrRegion } from "./types";

interface GuidedPanelViewProps {
  page: MangaPage;
  panel: MangaPanel | undefined;
  panelIndex: number;
  panelCount: number;
  pageCount: number;
  readingDirection: "rtl" | "ltr";
  loading: boolean;
  message: string | null;
  regions: OcrRegion[];
  activeRegionId?: string;
  activeRegion?: OcrRegion;
  supportPanelBounds: SupportPanelBounds | null;
  ocrRefreshing: boolean;
  selectionBusy: boolean;
  canGoBack: boolean;
  canGoForward: boolean;
  onBack: () => void;
  onForward: () => void;
  onClose: () => void;
  onSelect: (page: MangaPage, region: NormalizedRegion) => void;
  onOpenRegions: (regions: OcrRegion[]) => void;
}

function viewportSize(): { width: number; height: number } {
  return {
    width: window.visualViewport?.width ?? window.innerWidth,
    height: window.visualViewport?.height ?? window.innerHeight
  };
}

export function GuidedPanelView({
  page,
  panel,
  panelIndex,
  panelCount,
  pageCount,
  readingDirection,
  loading,
  message,
  regions,
  activeRegionId,
  activeRegion,
  supportPanelBounds,
  ocrRefreshing,
  selectionBusy,
  canGoBack,
  canGoForward,
  onBack,
  onForward,
  onClose,
  onSelect,
  onOpenRegions
}: GuidedPanelViewProps) {
  const [viewport, setViewport] = useState(viewportSize);
  const isRtl = readingDirection === "rtl";

  useEffect(() => {
    const update = () => setViewport(viewportSize());
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest("input, select, textarea, [contenteditable='true'], [role='textbox']")) return;
      const forwardKey = isRtl ? "ArrowLeft" : "ArrowRight";
      const backKey = isRtl ? "ArrowRight" : "ArrowLeft";
      if (event.key === forwardKey || event.key === "PageDown") {
        event.preventDefault();
        if (canGoForward) onForward();
      } else if (event.key === backKey || event.key === "PageUp") {
        event.preventDefault();
        if (canGoBack) onBack();
      } else if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [canGoBack, canGoForward, isRtl, onBack, onClose, onForward]);

  const imageStyle = useMemo<CSSProperties | undefined>(() => {
    if (!panel) return undefined;
    const fitted = fitMangaPanelToViewport(page, panel, viewport);
    const pageShift = activeRegion && supportPanelBounds
      ? readerPageOcclusionShift({
          pageLeft: fitted.left,
          pageTop: fitted.top,
          pageWidth: fitted.imageWidth,
          pageHeight: fitted.imageHeight,
          region: activeRegion,
          panel: supportPanelBounds
        })
      : 0;
    return {
      width: `${fitted.imageWidth}px`,
      height: `${fitted.imageHeight}px`,
      left: `${fitted.left}px`,
      top: `${fitted.top - pageShift}px`,
      clipPath: `inset(${panel.y * 100}% ${(1 - panel.x - panel.width) * 100}% ${(1 - panel.y - panel.height) * 100}% ${panel.x * 100}%)`,
      WebkitClipPath: `inset(${panel.y * 100}% ${(1 - panel.x - panel.width) * 100}% ${(1 - panel.y - panel.height) * 100}% ${panel.x * 100}%)`
    };
  }, [activeRegion, page.height, page.width, panel, supportPanelBounds, viewport.height, viewport.width]);

  const panelAspect = panel ? (page.width * panel.width) / (page.height * panel.height) : 0;
  const showRotateHint = viewport.height > viewport.width && panelAspect >= 1.3;

  return (
    <section
      className="guided-panel-view"
      data-reading-direction={readingDirection}
      data-reader-control
      aria-label="Guided panel view"
    >
      <header className="guided-panel-header">
        <span>Page {page.index + 1} of {pageCount}</span>
        <button type="button" onClick={onClose} aria-label="Close Guided View" title="Close Guided View"><X size={20} /></button>
      </header>

      {loading || !panel || !imageStyle ? (
        <div className="guided-panel-loading" role="status"><LoaderCircle className="spin" size={24} /> Finding this page’s panels…</div>
      ) : (
        <div className="guided-panel-image" style={imageStyle}>
          <img src={page.imageUrl} width={page.width} height={page.height} alt={`Page ${page.index + 1}, panel ${panelIndex + 1}`} draggable="false" />
          <AssistOverlay
            page={page}
            enabled
            ocrRefreshing={ocrRefreshing}
            selectionBusy={selectionBusy}
            regions={regions}
            activeRegionId={activeRegionId}
            onSelect={onSelect}
            onOpenRegions={onOpenRegions}
          />
        </div>
      )}

      {showRotateHint && (
        <div className="guided-rotate-hint" role="status">
          <RotateCw size={21} aria-hidden="true" />
          <span><strong>Turn for the full frame</strong><small>Rotate to landscape. Details will stay opposite the bubble you open.</small></span>
        </div>
      )}

      {message && <p className="guided-panel-message" role="status">{message}</p>}

      <footer className="guided-panel-controls">
        <button className="guided-panel-back" type="button" onClick={onBack} disabled={!canGoBack} aria-label="Previous panel" title="Previous panel">
          {isRtl ? <ChevronRight size={24} /> : <ChevronLeft size={24} />}
        </button>
        <span><strong>Panel {Math.min(panelIndex + 1, panelCount || 1)}</strong><small>of {Math.max(panelCount, 1)}</small></span>
        <button className="guided-panel-forward" type="button" onClick={onForward} disabled={!canGoForward} aria-label="Next panel" title="Next panel">
          {isRtl ? <ChevronLeft size={24} /> : <ChevronRight size={24} />}
        </button>
      </footer>
    </section>
  );
}
